import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/axios";
import { USER, COMMON } from "../config/api";
import toast from "react-hot-toast";


function Dashboard() {
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All"); // selected tab
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await apiClient.get(COMMON.GET_CATEGORY);
        if (data.success) {
          setCategories(data.items);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        toast.error(err.response?.data?.message || err.message || "Failed to fetch categories");
      }
    };
    fetchCategories();
  }, []);

  // 1. Fetch Menu Items from backend
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        let url = USER.MENU_ITEMS;
        if (activeCategory !== "All") {
          url += `?category=${activeCategory}`;
        }
        const { data } = await apiClient.get(url);
        if (data.success) {
          setMenuItems(data.items);
        } else {
          navigate('/login');
        }
      } catch (err) {
        console.error("Error fetching menu items:", err);
        toast.error(err.response?.data?.message || err.message || "Failed to fetch menu items");
      }
    };
    fetchMenu();
  }, [activeCategory]); 

  // 2. Add to Cart
  const addToCart = (item) => {
    setCart((prev) => {
      const exist = prev.find((c) => c._id === item._id);
      if (exist) {
        return prev.map((c) =>
          c._id === item._id ? { ...c, qty: c.qty + 1 } : c
        );
      } else {
        return [...prev, { ...item, qty: 1 }];
      }
    });
  };

  // 3. Update quantity
  const updateQty = (id, type) => {
    setCart((prev) =>
      prev
        .map((c) =>
          c._id === id
            ? { ...c, qty: type === "inc" ? c.qty + 1 : c.qty - 1 }
            : c
        )
        .filter((c) => c.qty > 0)
    );
  };

  // 4. Subtotal calculation
  useEffect(() => {
    const total = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
    setSubtotal(total);
  }, [cart]);

  // 5. Place Order
  const placeOrder = async () => {
  try {
    if (!token) {
      toast.error("You must be logged in to place an order");
      return;
    }

    const decoded = jwtDecode(token);
    
    const { data } = await apiClient.post(USER.PLACE_ORDER, {
      table_id: decoded.tableId,
      type: "dine-in", 
      subtotal,
      items: cart.map((c) => ({
        _id: c._id,
        qty: c.qty,
        price: c.price,
      })),
    });

    if (!data.success) throw new Error(data?.message || "Order failed");

    toast.success(data.message || "Order placed successfully!");
    setCart([]);
    setSubtotal(0);
    window.location.href = "/userpayment";
  } catch (err) {
    console.error("Error placing order:", err);
    toast.error(err.response?.data?.message || err.message || "Failed to place order");
  }
  };

  const handleLogout = () => {
  localStorage.removeItem("token");
  toast.success("You have been logged out.");
   navigate("/login");
};

  return (
    <>      
      <div className="index row">
        <div className="container-fluid p-0 col-sm-12 col-md-9">
          {/* Main */}
          <div className="main-content flex-grow-1">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-3">
                <h3 className="m-0">Restaurant POS</h3>
              </div>

              {token ? (
                <button className="btn btn-danger btn-sm" onClick={handleLogout}>
                  Logout
                </button>
              ) : (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => (window.location.href = "/login")}
                >
                  Login
                </button>
              )}
            </div>

            <div className="container mt-5">
              <div className="filter-right-group">
                <div className="filter-bar">  
                  <div className="">
                    <div className="category-tabs filter-right-group mt-3">
                      <button
                        className={`category-tab ${activeCategory === "All" ? "active" : ""}`}
                        onClick={() => setActiveCategory("All")}
                      >
                        All
                      </button>
                      {categories.map((cat) => (
                        <button
                          key={cat._id}
                          className={`category-tab ${
                            activeCategory === cat._id ? "active" : ""
                          }`}
                          onClick={() => setActiveCategory(cat._id)}
                        >
                            {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row menu-list">
              {menuItems.map((item) => (
                <div className="col-md-4 mb-4" key={item._id}>
                  <div className="dish-card">
                    <div className="image-wrapper">
                      <img
                        src={item.image || "https://via.placeholder.com/120"}
                        alt={item.name}
                      />
                    </div>
                    <div className="content">
                      <span className="subtitle">{item.category?.name || "Menu"}</span>
                      <div className="title-price">
                        <h2 className="title">{item.name}</h2>
                        <span className="price">Rs.{item.price}</span>
                      </div>
                      <div className="controls">
                        <button
                          className="add-btn"
                          data-id="{index}" 
                          data-img="{item.img}"
                          data-name="{item.name}"
                          data-price="{item.price.toFixed(2)}"
                          onClick={() => addToCart(item)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            </div>
          </div>
        </div>
        {/* Cart */}
        <div className="cart col-sm-12 col-md-3">
          <h5>Orders</h5>
          {cart.map((c) => (
            <div key={c._id} className="add-item-sec">
              <div className="dish-card">
                <div className="content">
                  <span className="subtitle">{c.category?.name || "Main Course"}</span>
                  <div className="title-price">
                    <h2 className="title">{c.name}</h2>
                    <span className="price">Rs.{c.price.toFixed(2)}</span>
                  </div>
                  <div className="controls">
                    <div className="qty-control">
                      <button onClick={() => updateQty(c._id, "dec")} className="qty-btn">−</button>
                      <span className="qty-number">{c.qty}</span>
                      <button onClick={() => updateQty(c._id, "inc")} className="qty-btn">+</button>
                    </div>
                    <button 
                      className="btn btn-sm remove-btn"
                      onClick={() => setCart(cart.filter((x) => x._id !== c._id))}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <hr className="text-secondary"/>
          <p>Sub Total: <span className="float-end">Rs.{subtotal.toFixed(2)}</span></p>
          <button onClick={placeOrder} className="btn btn-submit mt-3">Place Order</button>
        </div>

      </div>
    </>
  )
}

export default Dashboard